import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from '@pletly/ui';

import {
	TeamBasicInfoFragmentDoc,
	useCreateTeamMutation,
} from './__generated__/createTeam.mutation.generated';

type CreateTeamValues = {
	name: string;
};

export type UseCreateTeamProps = {
	organisationUuid: string;
	parentTeamUuid?: string;
	onTeamCreated: () => unknown;
	onError?: () => unknown;
};

export const useCreateTeam = ({
	organisationUuid,
	onTeamCreated,
	onError,
	parentTeamUuid,
}: UseCreateTeamProps) => {
	const [createTeam, { loading }] = useCreateTeamMutation();
	const { t } = useTranslation();

	const handleCreate = useCallback(
		async (values: CreateTeamValues) => {
			if (loading) {
				return;
			}

			try {
				await createTeam({
					variables: {
						data: {
							organisationUuid,
							parentTeamUuid,
							name: values.name,
						},
					},
					update: (cache, { data }) => {
						if (!data) return;
						const { organisation, ...teamData } = data.createTeam;
						cache.modify({
							id: cache.identify(organisation),
							fields: {
								teams(existingTeams = []) {
									const newTeamRef = cache.writeFragment({
										data: teamData,
										fragment: TeamBasicInfoFragmentDoc,
									});

									return [...existingTeams, newTeamRef];
								},
							},
						});
					},
				});
				Toast.show({ type: 'success', text1: t('toasts.success.team-created') });
				onTeamCreated();
			} catch (error) {
				Toast.show({ type: 'error', text1: t('toasts.error.something-wrong') });
				onError?.();
			}
		},
		[loading, createTeam, organisationUuid, parentTeamUuid, t, onTeamCreated, onError],
	);

	return {
		submit: handleCreate,
		loading,
	};
};
